// Screen 3 of the teams feature: every tie for one team, and the links the manager sends.
// See kumpula/TEAMS.md.
//
// NOTHING HERE CACHES A RUBBER'S TIME OR COURT. Venue admins move, reprice and cancel rubbers
// through their ordinary calendar as a requirement, not a concession - they are the ones who fix
// things when a court floods or a league reschedules. The backend re-joins every rubber from its
// reservation on each teams-managerteams call, and no mail is ever sent when an admin MOVES one,
// so this screen is the only way a manager finds out. Drift has to be loud, not merely accurate.
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Alert,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
import moment from 'moment'
import withAppconfig from '../../AppContext'
import TopNavigation from '../../TopNavigation'
import LottieLoading from '../../components/LottieLoading'
import { useManagerTeams } from '../../utils/useManagerTeams'
import { useTeamsApi } from '../../utils/teamsapi'
import { describeFormat } from '../../utils/teamformats'
import { confirmationLabel } from '../../utils/teamties'
import {
  teamContext,
  seasonWindow,
  formatDate,
  formatDateWeekday,
  formatDateRange,
  formatRubberTime,
  tallyFromRows,
  incompleteTies,
  acceptHost,
  offerLink,
  linkLines,
} from '../../utils/teamsview'
import { TeamsFailure, ChoiceGuidance, SpareNotice } from './TeamsScreen'

// A rubber the tie thinks it has but does not. Never counted as booked, and never quiet.
function RubberRow({ rubber, lang }) {
  const { t } = useTranslation()
  const held = rubber.status === 'booked'
  return (
    <div className={'d-flex justify-content-between align-items-center py-1' + (held ? '' : ' text-danger')}>
      <div>
        <strong>{rubber.typelabel}</strong>
        {held ? (
          <span>
            {' '}
            {/* No date: every rubber of a tie is on the tie's own date, which the card header
                already carries. */}
            {formatRubberTime(rubber.start, rubber.end, lang, { withDate: false })}
            {rubber.courtname ? ' - ' + rubber.courtname : ''}
          </span>
        ) : (
          <span> {t('teams_rubber_not_held')}</span>
        )}
      </div>
      {held ? null : (
        <Badge color='danger'>
          {rubber.status === 'cancelled' ? t('teams_rubber_cancelled') : t('teams_rubber_missing')}
        </Badge>
      )}
    </div>
  )
}

function TieCard({ tie, format, acceptLink, onCancel, cancelling, lang }) {
  const { t } = useTranslation()
  const tally = tallyFromRows(tie.rubbers, format)
  const confirmed = tie.confirmation && tie.confirmation.state === 'confirmed'

  return (
    <Card className='mb-3'>
      <CardHeader className='d-flex justify-content-between align-items-center'>
        <span>
          <strong className='text-capitalize'>{formatDateWeekday(tie.date, lang)}</strong>
          {tie.opponent ? ' - ' + tie.opponent : ''}
        </span>
        <Badge color={confirmed ? 'success' : 'secondary'}>{confirmationLabel(tie)}</Badge>
      </CardHeader>
      <CardBody>
        <div className='text-muted mb-2'>
          {tally.booked} / {tally.expected} {t('teams_booked')}
          {tally.gone ? ' (' + tally.gone + ' ' + t('teams_not_held') + ')' : ''}
        </div>

        {(tie.rubbers || []).length === 0 ? (
          <Alert color='secondary' className='mb-2'>
            {t('teams_tie_empty')}
          </Alert>
        ) : (
          (tie.rubbers || []).map((rubber, i) => (
            <RubberRow key={(rubber.reservationid || 'x') + i} rubber={rubber} lang={lang} />
          ))
        )}

        {confirmed && tie.confirmation.opponentteam ? (
          <div className='mt-2'>
            <strong>{t('teams_opponent_confirmed')}:</strong> {tie.confirmation.opponentteam}
          </div>
        ) : null}

        {/* The opponent's link. No callable returns it; the token comes back on the tie and the
            host from the venue config. */}
        {!confirmed && acceptLink ? (
          <div className='mt-2'>
            <div className='text-muted small text-break'>{acceptLink}</div>
            <CopyToClipboard text={acceptLink}>
              <Button size='sm' color='link' className='pl-0'>
                {t('teams_copy_link')}
              </Button>
            </CopyToClipboard>
          </div>
        ) : null}

        <div className='mt-2'>
          <Button size='sm' color='danger' outline disabled={cancelling} onClick={() => onCancel(tie)}>
            {cancelling ? <Spinner size='sm' /> : t('teams_cancel_tie')}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

function TeamSeasonScreen({ appconfig }) {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { teamid } = useParams()
  const api = useTeamsApi({ appconfig })
  const { loading, data, failure, reload } = useManagerTeams({ appconfig })
  const [busy, setBusy] = useState(null)
  const [pendingCancel, setPendingCancel] = useState(null)
  const [actionFailure, setActionFailure] = useState(null)
  const [sent, setSent] = useState(null)

  const { team, season, format, ties, quota } = teamContext(data, teamid)
  const today = moment().format('YYYY-MM-DD')
  const window = seasonWindow(season, today)
  const host = acceptHost(appconfig)
  const lines = linkLines(ties, { host, pathPrefix: appconfig.pathPrefix, lang: i18n.language })
  // One link that shows every candidate date. Any of the team's tokens renders the same menu, so
  // an opponent never has to try several links to find one still free.
  const offer = offerLink(ties, { host, pathPrefix: appconfig.pathPrefix })
  // Only the ties whose links are still to be sent matter here - a confirmed date is no longer
  // on offer, and an opponent who already answered is not going to reopen it.
  const unsentIncomplete = incompleteTies(
    ties.filter((tie) => lines.some((line) => line.tieid === tie.id)),
    format
  )

  const cancelTie = (tie) => {
    setActionFailure(null)
    setPendingCancel(null)
    setBusy(tie.id)
    api.cancelTie({ tieid: tie.id }).then((res) => {
      setBusy(null)
      if (!res.ok) {
        setActionFailure(res)
        return
      }
      reload()
    })
  }

  // Mails the link list to the team's OWN MANAGERS so they can forward it - the system never
  // emails an opponent. There is no idempotency key, so the button is disabled once it has run.
  const sendLinks = () => {
    setActionFailure(null)
    setBusy('links')
    api.sendLinks({ teamid }).then((res) => {
      setBusy(null)
      if (!res.ok) {
        setActionFailure(res)
        return
      }
      setSent(res.data)
    })
  }

  if (loading) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <LottieLoading width={150} height={150} />
        </Container>
      </div>
    )
  }

  if (!team) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <TeamsFailure failure={failure} />
          {!failure ? <Alert color='secondary' className='mt-3'>{t('teams_not_found')}</Alert> : null}
        </Container>
      </div>
    )
  }

  return (
    <div className='full-page-background'>
      <TopNavigation />
      <Container>
        <Row>
          <Col xs='12'>
            <h4 className='mt-3'>{team.name}</h4>
            <div className='text-muted'>{season ? season.name : ''}</div>
            {/* The season window is what bounds every tie date, and the booking window is when
                the manager may act at all - both worth stating rather than discovering. */}
            {season && formatDateRange(season.validstart, season.validend) ? (
              <div className='text-muted small'>
                {t('teams_season_active')} {formatDateRange(season.validstart, season.validend)}
              </div>
            ) : null}
            {season && formatDateRange(season.bookingopens, season.bookingcloses) ? (
              <div className='text-muted small'>
                {t('teams_booking_period')}:{' '}
                {formatDateRange(season.bookingopens, season.bookingcloses)}
              </div>
            ) : null}
            <div className='text-muted mb-3'>{describeFormat(format)}</div>
          </Col>
        </Row>
        <Row>
          <Col xs='12' md='8'>
            <TeamsFailure failure={actionFailure || failure} />
            <ChoiceGuidance team={team} quota={quota} />
            <SpareNotice quota={quota} />

            {window.canBook && team.state === 'active' ? (
              <Button
                color='primary'
                className='mt-3 mb-3'
                onClick={() => history.push(appconfig.pathPrefix + '/teams/' + teamid + '/tie')}
              >
                {t('teams_new_tie')}
              </Button>
            ) : null}
            <Button
              color='secondary'
              outline
              className='mt-3 mb-3 ml-2'
              onClick={() =>
                history.push(appconfig.pathPrefix + '/teams/' + teamid + '/availability')
              }
            >
              {t('teams_season_availability_title')}
            </Button>
            {window.state === 'closed' ? (
              <Alert color='secondary'>{t('teams_booking_closed')}</Alert>
            ) : null}

            {ties.length === 0 ? (
              <Card>
                <CardBody>{t('teams_no_ties')}</CardBody>
              </Card>
            ) : null}

            {ties
              .slice()
              .sort((a, b) => String(a.date).localeCompare(String(b.date)))
              .map((tie) => (
                <TieCard
                  key={tie.id}
                  tie={tie}
                  format={format}
                  acceptLink={(lines.find((l) => l.tieid === tie.id) || {}).url}
                  onCancel={setPendingCancel}
                  cancelling={busy === tie.id}
                  lang={i18n.language}
                />
              ))}
          </Col>

          <Col xs='12' md='4'>
            <Card className='mb-3'>
              <CardHeader>{t('teams_links_title')}</CardHeader>
              <CardBody>
                {/* Only worth saying when one of the ties actually on offer is short: the
                    opponent's page renders the rubbers as they stand when the link is opened, so
                    an unbooked tie shows them an empty page. Shown always, it would just be
                    noise the manager learns to skip. */}
                {unsentIncomplete.length ? (
                  <Alert color='warning' className='small'>
                    {t('teams_links_book_first').replace('{COUNT}', unsentIncomplete.length)}
                  </Alert>
                ) : null}

                {offer ? (
                  <div className='mb-3'>
                    <div className='small font-weight-bold'>{t('teams_offer_title')}</div>
                    <div className='small text-muted mb-1'>{t('teams_offer_help')}</div>
                    <div className='small text-break'>{offer}</div>
                    <CopyToClipboard text={offer}>
                      <Button size='sm' color='primary' className='mt-1'>
                        {t('teams_copy_link')}
                      </Button>
                    </CopyToClipboard>
                  </div>
                ) : null}

                {lines.length === 0 ? (
                  <div className='text-muted'>{t('teams_links_none')}</div>
                ) : (
                  <>
                    <div className='small font-weight-bold'>{t('teams_links_per_date')}</div>
                    <div className='small text-muted mb-1'>{t('teams_links_per_date_help')}</div>
                    <pre className='small text-break' style={{ whiteSpace: 'pre-wrap' }}>
                      {lines.map((l) => l.text).join('\n')}
                    </pre>
                    <CopyToClipboard text={lines.map((l) => l.text).join('\n')}>
                      <Button size='sm' color='secondary'>
                        {t('teams_copy_all')}
                      </Button>
                    </CopyToClipboard>
                    <Button
                      size='sm'
                      color='link'
                      disabled={busy === 'links' || !!sent}
                      onClick={sendLinks}
                    >
                      {busy === 'links' ? <Spinner size='sm' /> : t('teams_mail_links')}
                    </Button>
                    {sent ? (
                      <div className='text-success small mt-1'>
                        {t('teams_links_sent')
                          .replace('{SENT}', sent.sent)
                          .replace('{DATES}', sent.dates)}
                      </div>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Cancelling is irreversible - there is no un-cancel, and re-creating the date costs a
            fresh claim on the quota - so it asks first. */}
        <Modal isOpen={!!pendingCancel} toggle={() => setPendingCancel(null)}>
          <ModalHeader toggle={() => setPendingCancel(null)}>{t('teams_cancel_tie')}</ModalHeader>
          <ModalBody>
            {pendingCancel ? (
              <>
                <div className='mb-2'>
                  <strong>{formatDate(pendingCancel.date)}</strong>
                  {pendingCancel.opponent ? ' - ' + pendingCancel.opponent : ''}
                </div>
                <div>{t('teams_cancel_tie_what')}</div>

                {/* An opponent who has confirmed is expecting to play that date, and the system
                    never emails opponents about anything - so nobody tells them unless the
                    manager does. That is worth stopping over. */}
                {(pendingCancel.confirmation || {}).state === 'confirmed' ? (
                  <Alert color='danger' className='mt-3 mb-0'>
                    <strong>
                      {t('teams_cancel_tie_confirmed').replace(
                        '{OPPONENT}',
                        (pendingCancel.confirmation || {}).opponentteam || ''
                      )}
                    </strong>
                  </Alert>
                ) : null}
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color='secondary' onClick={() => setPendingCancel(null)}>
              {t('teams_back')}
            </Button>
            <Button color='danger' onClick={() => cancelTie(pendingCancel)}>
              {t('teams_cancel_tie_yes')}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}

export default withAppconfig(TeamSeasonScreen)
